import { useCallback, useMemo, useRef } from 'react';

import type { ApiError } from './api-client';

/**
 * A new `Idempotency-Key` for one user action (master X3, S2 R13): a random UUID v4.
 *
 * The CALLER of an action calls this once, before the first attempt, and passes the same value on
 * every retry of that action, including the replay after a token renewal. The API client never
 * creates a key itself, so it cannot give a retry a new one (AC23).
 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/**
 * True when the same request may be sent again with the same `Idempotency-Key`. The API stores
 * every answer below 500 and replays it for that key, and deletes the key on a 5xx (S2 R11): so
 * after a network failure, a timeout or a 5xx the key is kept and a retry replays or runs the
 * action once (S2 R13, AC23), while after a final 4xx the key is forgotten, since replaying it
 * would only return the same refusal. `IDEMPOTENCY_KEY_IN_PROGRESS` is the exception: the first
 * request is still running, so the same key fetches its answer.
 */
export function keepsKeyForRetry(error: ApiError): boolean {
  if (error.reasonCode === 'IDEMPOTENCY_KEY_IN_PROGRESS') return true;
  return error.status < 400 || error.status >= 500;
}

/** Keys of a screen's writes, one per action name. */
export interface ActionKeys {
  /** The key of `action`: the kept one on a retry, a new one for a new action. */
  readonly keyFor: (action: string) => string;
  /** After an answer: keeps the key of `action` only when a retry must replay it. */
  readonly settle: (action: string, error: ApiError | null) => void;
}

/**
 * How many actions keep a key at once:
 * - `perAction`: every action name keeps its own key (E10: the stop, each channel's retry);
 * - `one`: only the last action keeps a key; asking for another action forgets it (E7: a body
 *   changed since a failed save is a new action, M2).
 */
export type KeySlots = 'perAction' | 'one';

/**
 * Idempotency keys for a screen's writes (master X3, S2 R13). An action is named by the caller:
 * a route plus its body, or a row id. Its key is kept while a retry makes sense
 * ({@link keepsKeyForRetry}); a success or a final refusal forgets it, so the next tap is a new
 * action with a new key.
 */
export function useActionKeys(slots: KeySlots = 'perAction'): ActionKeys {
  const keys = useRef(new Map<string, string>());
  const keyFor = useCallback(
    (action: string) => {
      let key = keys.current.get(action);
      if (key === undefined) {
        if (slots === 'one') keys.current.clear();
        key = newIdempotencyKey();
        keys.current.set(action, key);
      }
      return key;
    },
    [slots],
  );
  const settle = useCallback((action: string, error: ApiError | null) => {
    if (error === null || !keepsKeyForRetry(error)) keys.current.delete(action);
  }, []);
  return useMemo(() => ({ keyFor, settle }), [keyFor, settle]);
}

/**
 * One key per request body, one body at a time (E7's saves): the same body keeps its key while a
 * retry makes sense; a different body, a success or a final refusal gets a new key next time.
 * A thin wrapper of {@link useActionKeys} with one slot, named by the body.
 */
export function useKeyPerBody(): {
  readonly keyFor: (body: unknown) => string;
  readonly settle: (error: ApiError | null) => void;
} {
  const keys = useActionKeys('one');
  const last = useRef<string | null>(null);
  const keyFor = useCallback(
    (body: unknown) => {
      last.current = JSON.stringify(body);
      return keys.keyFor(last.current);
    },
    [keys],
  );
  const settle = useCallback(
    (error: ApiError | null) => {
      if (last.current !== null) keys.settle(last.current, error);
    },
    [keys],
  );
  return useMemo(() => ({ keyFor, settle }), [keyFor, settle]);
}
